import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaEdit, FaPlus, FaTrash, FaTimes, FaCheckCircle, FaDoorOpen } from 'react-icons/fa';
import { apiCall } from '../../config/api';
import { RoomType, Facility } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

const FacilitiesRoomTypes: React.FC = () => {
        const { token } = useAuth();
        const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
        const [facilities, setFacilities] = useState<Facility[]>([]);
        const [selectedFacility, setSelectedFacility] = useState<string | number>('');
        const [loading, setLoading] = useState<boolean>(true);
        const [error, setError] = useState<string | null>(null);
        const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
        const [editingRoomType, setEditingRoomType] = useState<RoomType | null>(null);
        const [formData, setFormData] = useState({
                facility: '',
                name: '',
                description: '',
                color: '#3b82f6',
                is_active: true,
        });

        // Load facilities
        const loadFacilities = useCallback(async () => {
                try {
                        const response = await apiCall('/facilities/facilities/', { method: 'GET' }, token || undefined);
                        const data = await response.json();
                        const items = data.results || data;
                        setFacilities(Array.isArray(items) ? items : []);
                        // Set first facility as default if none selected
                        if (items.length > 0 && !selectedFacility) {
                                setSelectedFacility(items[0].id);
                        }
                } catch (err) {
                        console.error('Error loading facilities:', err);
                        setError(err instanceof Error ? err.message : 'Failed to load facilities');
                }
        }, [token, selectedFacility]);

        // Load room types
        const loadRoomTypes = useCallback(async () => {
                try {
                        setLoading(true);
                        setError(null);
                        const endpoint = selectedFacility 
                                ? `/facilities/room-types/?facility=${selectedFacility}`
                                : '/facilities/room-types/';
                        const response = await apiCall(endpoint, { method: 'GET' }, token || undefined);
                        const data = await response.json();
                        // Handle paginated response
                        const items = data.results || data;
                        setRoomTypes(Array.isArray(items) ? items : []);
                } catch (err) {
                        console.error('Error loading room types:', err);
                        setError(err instanceof Error ? err.message : 'Failed to load room types');
                } finally {
                        setLoading(false);
                }
        }, [token, selectedFacility]);

        useEffect(() => {
                loadFacilities();
        }, [loadFacilities]);

        useEffect(() => {
                if (selectedFacility) {
                        loadRoomTypes();
                }
        }, [loadRoomTypes, selectedFacility]);

        // Handle add new room type
        const handleAddRoomType = () => {
                setEditingRoomType(null);
                setFormData({
                        facility: selectedFacility?.toString() || '',
                        name: '',
                        description: '',
                        color: '#3b82f6',
                        is_active: true,
                });
                setIsModalOpen(true);
        };

        // Handle edit room type
        const handleEditRoomType = (roomType: RoomType) => {
                setEditingRoomType(roomType);
                setFormData({
                        facility: roomType.facility?.toString() || '',
                        name: roomType.name,
                        description: roomType.description || '',
                        color: roomType.color || '#3b82f6',
                        is_active: roomType.is_active,
                });
                setIsModalOpen(true);
        };

        // Handle form input change
        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
                const { name, value } = e.target;
                setFormData(prev => ({ ...prev, [name]: value }));
        };

        // Handle form submit
        const handleSubmit = async (e: React.FormEvent) => {
                e.preventDefault();
                try {
                        setError(null);
                        const endpoint = editingRoomType
                                ? `/facilities/room-types/${editingRoomType.id}/`
                                : '/facilities/room-types/';
                        
                        const method = editingRoomType ? 'PATCH' : 'POST';
                        
                        const response = await apiCall(endpoint, {
                                method,
                                body: JSON.stringify(formData),
                        }, token || undefined);

                        if (!response.ok) {
                                const errorData = await response.json().catch(() => ({}));
                                throw new Error(errorData.detail || errorData.message || 'Failed to save room type');
                        }

                        setIsModalOpen(false);
                        setEditingRoomType(null);
                        loadRoomTypes();
                } catch (err) {
                        console.error('Error saving room type:', err);
                        setError(err instanceof Error ? err.message : 'Failed to save room type');
                }
        };

        // Handle delete room type
        const handleDeleteRoomType = async (id: number) => {
                if (!window.confirm('Are you sure you want to delete this room type?')) {
                        return;
                }
                try {
                        setError(null);
                        const response = await apiCall(`/facilities/room-types/${id}/`, {
                                method: 'DELETE',
                        }, token || undefined);
                        
                        if (!response.ok) {
                                // Try to get error details from response
                                const errorData = await response.json().catch(() => ({}));
                                const errorMessage = errorData.detail || errorData.message || `Failed to delete room type (Status: ${response.status})`;
                                throw new Error(errorMessage);
                        }
                        
                        // Success - reload the list
                        await loadRoomTypes();
                } catch (err) {
                        console.error('Error deleting room type:', err);
                        setError(err instanceof Error ? err.message : 'Failed to delete room type');
                }
        };

        // Handle reactivate room type
        const handleReactivateRoomType = async (id: number) => {
                if (!window.confirm('Are you sure you want to reactivate this room type?')) {
                        return;
                }
                try {
                        setError(null);
                        const response = await apiCall(`/facilities/room-types/${id}/`, {
                                method: 'PATCH',
                                body: JSON.stringify({ is_active: true }),
                        }, token || undefined);

                        if (!response.ok) {
                                const errorData = await response.json().catch(() => ({}));
                                throw new Error(errorData.detail || errorData.message || 'Failed to reactivate room type');
                        }

                        loadRoomTypes();
                } catch (err) {
                        console.error('Error reactivating room type:', err);
                        setError(err instanceof Error ? err.message : 'Failed to reactivate room type');
                }
        };

        return (
                <div className="p-6">
                        <div className="flex justify-between items-center mb-6">
                                <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
                                        <FaDoorOpen className="text-primary" />
                                        Room Types
                                </h1>
                                <button
                                        className="btn btn-primary"
                                        onClick={handleAddRoomType}
                                        disabled={!selectedFacility}
                                >
                                        <FaPlus className="mr-2" /> Add Room Type
                                </button>
                        </div>

                        {/* Facility Filter */}
                        <div className="mb-6">
                                <label className="label">
                                        <span className="label-text font-semibold">Select Facility</span>
                                </label>
                                <select
                                        className="select select-bordered w-full max-w-md"
                                        value={selectedFacility}
                                        onChange={(e) => setSelectedFacility(e.target.value)}
                                >
                                        {facilities.map((facility) => (
                                                <option key={facility.id} value={facility.id}>
                                                        {facility.name}
                                                </option>
                                        ))}
                                </select>
                        </div>

                        {error && (
                                <div className="alert alert-error mb-4">
                                        <span>{error}</span>
                                </div>
                        )}

                        {loading ? (
                                <div className="flex justify-center items-center py-12">
                                        <div className="loading loading-spinner loading-lg"></div>
                                </div>
                        ) : roomTypes.length === 0 ? (
                                <div className="text-center py-12">
                                        <p className="text-base-content opacity-60">No room types found. Create your first room type to get started.</p>
                                        <button
                                                className="btn btn-primary mt-4"
                                                onClick={handleAddRoomType}
                                                disabled={!selectedFacility}
                                        >
                                                <FaPlus className="mr-2" /> Create Your First Room Type
                                        </button>
                                </div>
                        ) : (
                                <div className="relative overflow-x-auto rounded-lg">
                                        <table className="table table-zebra bg-base-100/50">
                                                <thead>
                                                        <tr>
                                                                <th>Room Type Name</th>
                                                                <th className="text-center">Colour</th>
                                                                <th>Status</th>
                                                                <th className="w-24">Actions</th>
                                                        </tr>
                                                </thead>
                                                <tbody>
                                                        {roomTypes.map((roomType) => (
                                                                <tr key={roomType.id}>
                                                                        <td>{roomType.name}</td>
                                                                        <td>
                                                                                <div className="flex justify-center items-center">
                                                                                        <div 
                                                                                                className="w-6 h-6 rounded border border-base-300"
                                                                                                style={{ backgroundColor: roomType.color || '#3b82f6' }}
                                                                                                title={roomType.color || 'No colour set'}
                                                                                        />
                                                                                </div>
                                                                        </td>
                                                                        <td>
                                                                                <span className={`badge ${roomType.is_active ? 'badge-success' : 'badge-error'}`}>
                                                                                        {roomType.is_active ? 'Active' : 'Inactive'}
                                                                                </span>
                                                                        </td>
                                                                        <td>
                                                                                <div className="flex gap-2">
                                                                                        <button
                                                                                                className="btn btn-ghost btn-xs"
                                                                                                onClick={() => handleEditRoomType(roomType)}
                                                                                                title="Edit"
                                                                                        >
                                                                                                <FaEdit className="w-4 h-4" />
                                                                                        </button>
                                                                                        {roomType.is_active ? (
                                                                                                <button
                                                                                                        className="btn btn-ghost btn-xs text-error"
                                                                                                        onClick={() => handleDeleteRoomType(roomType.id)}
                                                                                                        title="Delete"
                                                                                                >
                                                                                                        <FaTrash className="w-4 h-4" />
                                                                                                </button>
                                                                                        ) : (
                                                                                                <button
                                                                                                        className="btn btn-ghost btn-xs text-success"
                                                                                                        onClick={() => handleReactivateRoomType(roomType.id)}
                                                                                                        title="Reactivate"
                                                                                                >
                                                                                                        <FaCheckCircle className="w-4 h-4" />
                                                                                                </button>
                                                                                        )}
                                                                                </div>
                                                                        </td>
                                                                </tr>
                                                        ))}
                                                </tbody>
                                        </table>
                                </div>
                        )}

                        {/* Add/Edit Room Type Modal */}
                        {isModalOpen && (
                                <div className="modal modal-open">
                                        <div className="modal-box w-11/12 max-w-2xl">
                                                <div className="flex justify-between items-center mb-4">
                                                        <h3 className="font-bold text-lg">
                                                                {editingRoomType ? 'Edit Room Type' : 'Add New Room Type'}
                                                        </h3>
                                                        <button
                                                                className="btn btn-sm btn-circle btn-ghost"
                                                                onClick={() => {
                                                                        setIsModalOpen(false);
                                                                        setEditingRoomType(null);
                                                                }}
                                                        >
                                                                <FaTimes />
                                                        </button>
                                                </div>

                                                <form onSubmit={handleSubmit}>
                                                        <div className="grid grid-cols-1 gap-4">
                                                                {/* Facility */}
                                                                <div className="form-control">
                                                                        <label className="label">
                                                                                <span className="label-text">Facility *</span>
                                                                        </label>
                                                                        <select
                                                                                name="facility"
                                                                                value={formData.facility}
                                                                                onChange={handleInputChange}
                                                                                className="select select-bordered"
                                                                                required
                                                                        >
                                                                                <option value="">Select a facility...</option>
                                                                                {facilities.map((facility) => (
                                                                                        <option key={facility.id} value={facility.id}>
                                                                                                {facility.name}
                                                                                        </option>
                                                                                ))}
                                                                        </select>
                                                                </div>

                                                                {/* Name */}
                                                                <div className="form-control">
                                                                        <label className="label">
                                                                                <span className="label-text">Room Type Name *</span>
                                                                        </label>
                                                                        <input
                                                                                type="text"
                                                                                name="name"
                                                                                value={formData.name}
                                                                                onChange={handleInputChange}
                                                                                className="input input-bordered"
                                                                                placeholder="e,g. Operating Room"
                                                                                required
                                                                        />
                                                                </div>

                                                                {/* Color */}
                                                                <div className="form-control">
                                                                        <label className="label">
                                                                                <span className="label-text">Color (for calendar display)</span>
                                                                        </label>
                                                                        <div className="flex gap-4 items-center">
                                                                                <input
                                                                                        type="color"
                                                                                        name="color"
                                                                                        value={formData.color}
                                                                                        onChange={handleInputChange}
                                                                                        className="w-24 h-12 cursor-pointer rounded-lg border-2 border-base-300"
                                                                                />
                                                                                <input
                                                                                        type="text"
                                                                                        name="color"
                                                                                        value={formData.color}
                                                                                        onChange={handleInputChange}
                                                                                        className="input input-bordered flex-1"
                                                                                        placeholder="#3b82f6"
                                                                                        pattern="^#[0-9A-Fa-f]{6}$"
                                                                                />
                                                                        </div>
                                                                </div>

                                                                {/* Description */}
                                                                <div className="form-control">
                                                                        <label className="label">
                                                                                <span className="label-text">Description</span>
                                                                        </label>
                                                                        <textarea
                                                                                name="description"
                                                                                value={formData.description}
                                                                                onChange={handleInputChange}
                                                                                className="textarea textarea-bordered"
                                                                                rows={3}
                                                                                placeholder="Describe this room type..."
                                                                        />
                                                                </div>

                                                                {/* Active Checkbox */}
                                                                <div className="form-control">
                                                                        <label className="label cursor-pointer justify-start gap-2">
                                                                                <input
                                                                                        type="checkbox"
                                                                                        name="is_active"
                                                                                        checked={formData.is_active}
                                                                                        onChange={(e) =>
                                                                                                setFormData({ ...formData, is_active: e.target.checked })
                                                                                        }
                                                                                        className="checkbox checkbox-primary"
                                                                                />
                                                                                <span className="label-text">Active</span>
                                                                        </label>
                                                                </div>
                                                        </div>

                                                        <div className="modal-action">
                                                                <button
                                                                        type="button"
                                                                        className="btn btn-ghost"
                                                                        onClick={() => {
                                                                                setIsModalOpen(false);
                                                                                setEditingRoomType(null);
                                                                        }}
                                                                >
                                                                        Cancel
                                                                </button>
                                                                <button type="submit" className="btn btn-primary">
                                                                        {editingRoomType ? 'Update Room Type' : 'Add Room Type'}
                                                                </button>
                                                        </div>
                                                </form>
                                        </div>
                                </div>
                        )}
                </div>
        );
};

export default FacilitiesRoomTypes;